<template>
    <Page class="help docs-page-layout">
        <button
            v-if="isNavCollapsed"
            type="button"
            class="floating-nav-toggle floating-nav-toggle--collapsed"
            @click="isNavCollapsed = false"
            aria-label="Show documentation navigation"
        >
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path
                    :fill="'var(--text-color)'"
                    fill-rule="evenodd"
                    d="M10 7h8a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-8V7ZM9 7H6a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3V7ZM4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8Z"
                    clip-rule="evenodd"
                />
            </svg>
        </button>

        <div class="docs-layout" :class="{ 'menu-collapsed': isNavCollapsed }">
            <aside class="docs-side">
                <Card class="content-card side-card nav-card">
                    <template #content>
                        <ul v-if="navLinks.length > 0" class="nav-list">
                            <li
                                v-for="link in navLinks"
                                :key="link.to"
                                class="nav-item"
                                :class="{ active: isLinkActive(link.to) }"
                            >
                                <RouterLink :to="link.to">
                                    {{ link.label }}
                                </RouterLink>
                            </li>
                        </ul>

                        <details
                            v-for="group in navGroups"
                            :key="group.label"
                            class="nav-group"
                            :open="group.open ?? true"
                        >
                            <summary>{{ group.label }}</summary>
                            <ul class="nav-sub-list">
                                <li
                                    v-for="item in group.items"
                                    :key="item.to"
                                    class="nav-item"
                                    :class="{ active: isLinkActive(item.to) }"
                                >
                                    <RouterLink :to="item.to">
                                        {{ item.label }}
                                    </RouterLink>
                                </li>
                            </ul>
                        </details>
                    </template>
                </Card>
            </aside>

            <div class="docs-main">
                <div class="welcome-header">
                    <h1>{{ title }}</h1>
                    <p v-if="subtitle" class="subtitle">{{ subtitle }}</p>
                </div>

                <slot />
            </div>

            <aside v-if="tocItems.length > 0" class="docs-toc">
                <Card class="content-card side-card toc-card">
                    <template #title>
                        <h2>On This Page</h2>
                    </template>
                    <template #content>
                        <ul class="toc-list">
                            <li v-for="item in tocItems" :key="item.id">
                                <a
                                    :href="`#${item.id}`"
                                    :class="{ active: activeTocId === item.id }"
                                >
                                    {{ item.label }}
                                </a>
                            </li>
                        </ul>
                    </template>
                </Card>
            </aside>
        </div>
    </Page>
</template>

<script setup lang="ts">
import Card from "primevue/card";
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

import Page from "@/components/Page.vue";

export interface DocsNavLink {
    label: string;
    to: string;
}

export interface DocsNavGroup {
    label: string;
    items: DocsNavLink[];
    open?: boolean;
}

export interface DocsTocItem {
    id: string;
    label: string;
}

const props = withDefaults(
    defineProps<{
        title: string;
        subtitle?: string;
        navLinks?: DocsNavLink[];
        navGroups?: DocsNavGroup[];
        tocItems?: DocsTocItem[];
        activeNavTo?: string;
    }>(),
    {
        subtitle: "",
        navLinks: () => [],
        navGroups: () => [],
        tocItems: () => [],
        activeNavTo: "",
    }
);

const isNavCollapsed = ref(false);
const activeTocId = ref(props.tocItems[0]?.id ?? "");
let tocObserver: IntersectionObserver | null = null;
const intersectionScores = new Map<string, number>();

const isLinkActive = (to: string) => props.activeNavTo === to;

const onHeaderMenuToggle = () => {
    isNavCollapsed.value = !isNavCollapsed.value;
};

const getFallbackSection = () => {
    const current = props.tocItems
        .map((item) => item.id)
        .filter((id) => {
            const el = document.getElementById(id);
            return el ? el.getBoundingClientRect().top <= 140 : false;
        })
        .pop();

    return current ?? props.tocItems[0]?.id ?? "";
};

const disconnectObserver = () => {
    tocObserver?.disconnect();
    tocObserver = null;
    intersectionScores.clear();
};

const setupTocObserver = () => {
    if (props.tocItems.length === 0) {
        activeTocId.value = "";
        return;
    }

    activeTocId.value = props.tocItems[0].id;

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
        return;
    }

    tocObserver = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const id = (entry.target as HTMLElement).id;
                intersectionScores.set(
                    id,
                    entry.isIntersecting ? entry.intersectionRatio : 0
                );
            });

            const activeFromIntersection = [...intersectionScores.entries()]
                .sort((a, b) => b[1] - a[1])
                .find(([, ratio]) => ratio > 0)?.[0];

            activeTocId.value = activeFromIntersection ?? getFallbackSection();
        },
        {
            root: null,
            rootMargin: "-20% 0px -60% 0px",
            threshold: [0, 0.25, 0.5, 0.75, 1],
        }
    );

    props.tocItems.forEach((item) => {
        const el = document.getElementById(item.id);
        if (el) {
            tocObserver?.observe(el);
            intersectionScores.set(item.id, 0);
        }
    });
};

onMounted(async () => {
    window.addEventListener("mystekz-docs-nav-toggle", onHeaderMenuToggle);
    await nextTick();
    setupTocObserver();
});

watch(
    () => props.tocItems.map((item) => item.id).join("|"),
    async () => {
        disconnectObserver();
        await nextTick();
        setupTocObserver();
    }
);

onBeforeUnmount(() => {
    window.removeEventListener("mystekz-docs-nav-toggle", onHeaderMenuToggle);
    disconnectObserver();
});
</script>

<style lang="scss" scoped>
.help {
    max-width: none;
    width: 100%;
    color: var(--text-color);
    background: color-mix(in srgb, var(--stekz-primary-color), white 96%);
    border-radius: 0;
    padding: 1.2rem;
    font-family: inherit;
    position: relative;

    .welcome-header {
        text-align: left;
        margin-bottom: 1rem;

        h1 {
            font-size: var(--text-xxl);
            margin-bottom: 0.35rem;
            color: var(--text-color);
            font-weight: 700;
        }

        .subtitle {
            color: var(--text-color-secondary);
            margin: 0;
            font-weight: 600;
        }
    }
}

.docs-main {
    min-width: 0;

    :deep(.content-card) {
        margin-bottom: var(--default-gap);
        background-color: transparent;
        border: 0;
        color: var(--text-color);
        box-shadow: none;
    }

    :deep(.content-card.introduction) {
        background: color-mix(in srgb, var(--stekz-primary-color), white 95%);
        border-radius: 0;
        padding-left: 0.8rem;
    }

    :deep(.p-card-title h2) {
        font-size: inherit;
        color: var(--text-color);
        font-weight: 700;
    }

    :deep(.p-card-content) {
        p {
            color: var(--text-color);
            margin-bottom: 1rem;
        }

        ol,
        ul {
            padding-left: 2rem;

            li {
                margin-bottom: 0.5rem;
            }
        }

        pre {
            background: color-mix(
                in srgb,
                var(--stekz-primary-color),
                white 96%
            );
            border: 1px solid var(--stekz-outline-variant);
            border-radius: 0;
            padding: 0.75rem;
            overflow-x: auto;
            color: var(--text-color);
        }

        table {
            width: 100%;
            border-collapse: collapse;

            th,
            td {
                text-align: left;
                border-bottom: 1px solid var(--stekz-outline-variant);
                padding: 0.5rem;
                vertical-align: top;
            }
        }

        a {
            color: var(--stekz-primary-color);
        }
    }

    :deep(.p-card-body) {
        padding: 0;
    }

    :deep(.p-card-title) {
        margin-bottom: 0.65rem;
    }
}

.docs-layout {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr) 240px;
    gap: var(--default-gap);
    align-items: start;
}

.docs-layout.menu-collapsed .docs-side {
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
}

.docs-side {
    position: sticky;
    top: 1rem;
    align-self: start;
    padding-right: 1rem;
    border-right: 1px solid var(--stekz-outline-variant);
    transition: opacity 0.18s ease;
}

.side-card {
    margin-bottom: 0;
}

.nav-card {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;

    :deep(.p-card-title) h2 {
        font-size: 0.95rem;
        color: var(--text-color-secondary);
    }
}

.nav-list {
    list-style: none;
    padding-left: 0 !important;
    margin: 0;
}

.nav-item {
    margin-bottom: 0.5rem;

    a {
        display: block;
        padding: 0.35rem 0.55rem;
        border-radius: 0;
        text-decoration: none;
        color: var(--text-color);
        font-size: 0.95rem;
        transition:
            background-color 0.15s ease,
            color 0.15s ease;
    }

    a:hover {
        background: color-mix(in srgb, var(--stekz-primary-color), white 92%);
    }
}

.nav-item.active a {
    background: color-mix(in srgb, var(--stekz-primary-color), white 88%);
    color: var(--text-color);
    font-weight: 600;
}

.docs-layout.menu-collapsed .nav-item.active a {
    background: transparent;
    color: var(--text-color);
    font-weight: 400;
}

.nav-group {
    margin-top: 0.25rem;

    summary {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        list-style: none;
        color: var(--text-color);
        font-size: 0.95rem;
        font-weight: 600;
        margin: 0.2rem 0 0.35rem;
        padding: 0.35rem 0.55rem;
        border-radius: 0;
        transition: background-color 0.15s ease;
    }

    summary::-webkit-details-marker {
        display: none;
    }

    summary::after {
        content: "▸";
        color: var(--text-color-secondary);
        font-size: 0.85rem;
        margin-left: 0.4rem;
        line-height: 1;
        transform: rotate(0deg);
        transition: transform 0.15s ease;
    }

    summary:hover {
        background: color-mix(in srgb, var(--stekz-primary-color), white 92%);
    }

    &[open] summary {
        background: color-mix(in srgb, var(--stekz-primary-color), white 88%);
    }

    &[open] summary::after {
        transform: rotate(90deg);
    }
}

.nav-sub-list {
    list-style: none;
    margin: 0 0 0 0.55rem;
    padding: 0 0 0 0.6rem;
    border-left: 1px solid var(--stekz-outline-variant);

    .nav-item a {
        color: var(--text-color-secondary);
        font-weight: 500;
    }
}

.docs-toc {
    position: sticky;
    top: 1rem;
    align-self: start;
    padding-left: 1rem;
}

.toc-list {
    list-style: none;
    padding-left: 0 !important;
    margin: 0;

    li {
        margin-bottom: 0.5rem;
    }

    a {
        color: var(--text-color-secondary);
        text-decoration: none;
        font-size: 0.95rem;
    }

    a:hover {
        color: var(--stekz-primary-color);
    }

    a.active {
        color: var(--stekz-primary-color);
        font-weight: 600;
    }
}

.toc-card {
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;

    :deep(.p-card-title) h2 {
        font-size: 0.95rem;
        color: var(--text-color-secondary);
    }

    :deep(.p-card-content) {
        border-left: 2px solid
            color-mix(in srgb, var(--stekz-primary-color), white 40%);
        padding-left: 0.7rem;
    }
}

.floating-nav-toggle {
    position: fixed;
    top: 0.75rem;
    left: 0.5rem;
    width: 40px;
    height: 40px;
    border-radius: 0;
    border: 1px solid var(--stekz-outline-variant);
    background: var(--surface-background-mild);
    color: var(--text-color-secondary);
    cursor: pointer;
    z-index: 1000;
    line-height: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.floating-nav-toggle--collapsed {
    z-index: 1100;
}

@media screen and (max-width: 1280px) {
    .docs-layout {
        grid-template-columns: 1fr;
    }

    .docs-side {
        position: static;
        border-right: 0;
        padding-right: 0;
    }

    .docs-layout.menu-collapsed .docs-side {
        display: none;
    }

    .docs-toc {
        position: static;
        padding-left: 0;
    }
}

@media screen and (max-width: 768px) {
    .help {
        padding: 0.5rem;

        .welcome-header {
            h1 {
                font-size: 1.5rem;
            }
        }

        :deep(.p-card-title h2) {
            font-size: 1.3rem;
        }

        :deep(.p-card-content table) {
            display: block;
            overflow-x: auto;
            white-space: nowrap;
        }
    }

    .docs-layout {
        gap: 0.75rem;
    }

    .docs-toc {
        display: none;
    }
}
</style>
